import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../utils/axiosConfig';
import { Flotante } from './ui';

const ROSTRO = {
    aparato: 'rostro tomado en un aparato',
    ficha: 'con la foto de la ficha',
};

const Grupo = ({ titulo, nota, personas, detalle }) => {
    if (!personas.length) return null;
    return (
        <section className="px-5 py-4">
            <h3 className="text-sm font-semibold text-slate-900">{titulo} <span className="text-slate-400 tabular-nums">{personas.length}</span></h3>
            {nota && <p className="text-xs text-slate-500 mt-0.5">{nota}</p>}
            <ul className="mt-2 space-y-1.5">
                {personas.map((p) => (
                    <li key={p.cedula} className="text-sm flex items-baseline justify-between gap-3">
                        <span className="text-slate-800 truncate">{p.nombre}</span>
                        <span className="text-xs text-slate-500 whitespace-nowrap">{detalle(p)}</span>
                    </li>
                ))}
            </ul>
        </section>
    );
};

// Panel lateral: qué se va a cargar y qué se va a borrar en el aparato, antes de aplicarlo
const SincronizacionAparato = ({ aparato, onCerrar, onCambio }) => {
    const [dif, setDif] = useState(null);
    const [error, setError] = useState(null);
    const [aplicando, setAplicando] = useState(false);
    const cerrarRef = useRef(null);

    useEffect(() => {
        axiosInstance.get(`/api/asistencia/aparatos/${aparato.id}/sincronizacion`)
            .then(({ data }) => setDif(data))
            .catch(() => setError('No se pudo calcular qué cambia en el aparato.'));
    }, [aparato.id]);

    // onCerrar cambia en cada refresco de la pantalla: se lee por ref para no
    // devolver el foco al botón de cerrar cada 5 segundos
    const onCerrarRef = useRef(onCerrar);
    onCerrarRef.current = onCerrar;
    useEffect(() => {
        cerrarRef.current?.focus();
        const alTeclear = (e) => e.key === 'Escape' && onCerrarRef.current();
        window.addEventListener('keydown', alTeclear);
        return () => window.removeEventListener('keydown', alTeclear);
    }, []);

    const aplicar = async () => {
        setAplicando(true);
        try {
            await axiosInstance.post(`/api/asistencia/aparatos/${aparato.id}/sincronizar`);
            toast.success(`Cambios enviados a ${aparato.nombre}`);
            onCambio();
            onCerrar();
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudieron enviar los cambios');
            setAplicando(false);
        }
    };

    const hayCambios = dif && (dif.faltan.length || dif.completar.length || dif.sobran.length || dif.acceso?.length);
    const soloFinDeSemana = dif?.fin_de_semana_solo || [];

    return (
        <Flotante>
        <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-labelledby="sinc-titulo">
            <div className="absolute inset-0 bg-slate-900/30" onClick={onCerrar} />
            <div className="relative w-full max-w-lg h-full bg-white shadow-xl flex flex-col">
                <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-4">
                    <div>
                        <h2 id="sinc-titulo" className="text-lg font-semibold text-slate-900">Cambios en {aparato.nombre}</h2>
                        <p className="text-sm text-slate-500 mt-0.5">
                            Todos los activos van en todos los aparatos, menos los de Foráneo.
                        </p>
                    </div>
                    <button
                        ref={cerrarRef} onClick={onCerrar} aria-label="Cerrar"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
                    {error && <p className="p-5 text-sm text-rose-600">{error}</p>}
                    {!dif && !error && (
                        <div className="flex justify-center p-10"><Loader2 size={24} className="animate-spin text-slate-400" /></div>
                    )}
                    {dif && !dif.leido && (
                        <p className="p-5 text-sm text-slate-600">Primero trae los usuarios del aparato para poder comparar.</p>
                    )}
                    {dif && dif.leido && !hayCambios && (
                        <p className="p-5 text-sm text-slate-600">El aparato está al día: no hay nada que cargar ni que borrar.</p>
                    )}
                    {dif && dif.leido && (
                        <>
                            <Grupo
                                titulo="Se borran" personas={dif.sobran}
                                nota="Egresados y foráneos que siguen cargados."
                                detalle={(p) => (p.motivo === 'Egresado' && p.fecha_egreso ? `Egresado el ${p.fecha_egreso}` : p.motivo)}
                            />
                            <Grupo
                                titulo="Se cargan" personas={dif.faltan}
                                nota="Activos que no están en el aparato. Sin rostro quedan con nombre y cédula, y el rostro se les toma en el aparato."
                                detalle={(p) => ROSTRO[p.rostro] || 'sin rostro'}
                            />
                            <Grupo
                                titulo="Se les carga el rostro" personas={dif.completar}
                                nota="Ya están en el aparato pero sin rostro."
                                detalle={(p) => ROSTRO[p.rostro]}
                            />
                            <Grupo
                                titulo="Se les ajusta el acceso" personas={dif.acceso || []}
                                nota={`Sábado y domingo solo pasan ${soloFinDeSemana.join(', ').replace(/, ([^,]*)$/, ' y $1')}; el resto, de lunes a viernes.`}
                                detalle={(p) => (p.fin_de_semana ? 'Todos los días' : 'De lunes a viernes')}
                            />
                        </>
                    )}
                </div>

                {hayCambios ? (
                    <div className="p-5 border-t border-slate-100">
                        <button
                            onClick={aplicar} disabled={aplicando || !aparato.conectado}
                            className="w-full h-10 rounded-xl bg-sura-blue text-white text-sm font-medium hover:bg-sura-blue-dark disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                        >
                            {aplicando ? 'Enviando...' : 'Aplicar los cambios en el aparato'}
                        </button>
                        {!aparato.conectado && (
                            <p className="text-xs text-slate-500 mt-2 text-center">El aparato tiene que estar conectado.</p>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
        </Flotante>
    );
};

export default SincronizacionAparato;
